/**
 * Material model: decor (visual) ≠ board product (physical) ≠ supplier article (sellable).
 * Lengths are integer µm, money is integer minor units (1 BAM = 100 feninga).
 */

export type Currency = 'BAM' | 'EUR';

export interface Money {
  /** Integer minor units. */
  minor: number;
  currency: Currency;
}

/** Unknown price is explicit — never treated as zero. */
export type Price = Money | { unknown: true; reason: string };

export function isKnownPrice(p: Price): p is Money {
  return !('unknown' in p);
}

export function addMoney(a: Money, b: Money): Money {
  if (a.currency !== b.currency) throw new Error(`Valute se razlikuju: ${a.currency} / ${b.currency}`);
  return { minor: a.minor + b.minor, currency: a.currency };
}

export function formatMoney(m: Money, locale = 'bs-BA'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: m.currency }).format(m.minor / 100);
}

export interface TextureMap {
  assetId: string | null;
  /** Physical coverage of one texture tile, µm. */
  coverage: { u: number; v: number };
  colorSpace: 'srgb' | 'linear';
}

export interface MaterialDecor {
  id: string;
  manufacturer: string;
  code: string;
  name: string;
  finish: 'matt' | 'satin' | 'gloss' | 'structured' | 'wood-pore';
  /** Hex sRGB fallback colour for preview. */
  baseColor: string;
  roughness: number;
  /** Wood-like decors have a grain; the grain runs along texture V. */
  hasGrain: boolean;
  /** Procedural demo pattern (no external texture). */
  procedural?: { kind: 'wood' | 'solid' | 'concrete'; ringColor?: string; scaleMm?: number };
  maps: { baseColor?: TextureMap; normal?: TextureMap; roughness?: TextureMap };
  isDemo: boolean;
}

export interface BoardProduct {
  id: string;
  decorId: string;
  substrate: 'chipboard' | 'mdf' | 'hdf' | 'plywood' | 'solid' | 'other';
  thickness: number;
  sheet: { length: number; width: number };
  doubleSided: boolean;
  grainAlongSheetLength: boolean;
  densityKgM3?: number;
}

export interface EdgeBandProduct {
  id: string;
  code: string;
  manufacturer: string;
  decorId: string | null;
  material: 'abs' | 'pvc' | 'pp' | 'melamine' | 'veneer';
  thickness: number;
  width: number;
  finish: string;
  pricePerMetre: Price;
}

export interface SupplierArticle {
  id: string;
  supplierId: string;
  localCode: string;
  productKind: 'board' | 'edge';
  productId: string;
  price: Price;
  unit: 'm2' | 'sheet' | 'm' | 'piece';
  taxRatePermille: number | null;
  availability: 'in-stock' | 'on-order' | 'unknown';
  checkedAt: string | null;
}

/** Original procedural demo decors. Not real Egger/Kronospan/etc. products. */
export const DEMO_DECORS: MaterialDecor[] = [
  { id: 'demo-white-matt', manufacturer: 'Demo', code: 'DEMO-W01', name: 'Demo bijela mat', finish: 'matt', baseColor: '#f2f1ec', roughness: 0.75, hasGrain: false, procedural: { kind: 'solid' }, maps: {}, isDemo: true },
  { id: 'demo-anthracite', manufacturer: 'Demo', code: 'DEMO-A02', name: 'Demo antracit', finish: 'matt', baseColor: '#3b3d40', roughness: 0.7, hasGrain: false, procedural: { kind: 'solid' }, maps: {}, isDemo: true },
  { id: 'demo-oak-natural', manufacturer: 'Demo', code: 'DEMO-O10', name: 'Demo hrast prirodni', finish: 'wood-pore', baseColor: '#c49a6c', roughness: 0.6, hasGrain: true, procedural: { kind: 'wood', ringColor: '#9c7448', scaleMm: 600 }, maps: {}, isDemo: true },
  { id: 'demo-walnut', manufacturer: 'Demo', code: 'DEMO-N20', name: 'Demo orah tamni', finish: 'wood-pore', baseColor: '#6b4a33', roughness: 0.55, hasGrain: true, procedural: { kind: 'wood', ringColor: '#4a2f1f', scaleMm: 600 }, maps: {}, isDemo: true },
  { id: 'demo-sage', manufacturer: 'Demo', code: 'DEMO-S30', name: 'Demo zelena kadulja', finish: 'satin', baseColor: '#9aa58c', roughness: 0.45, hasGrain: false, procedural: { kind: 'solid' }, maps: {}, isDemo: true },
  { id: 'demo-concrete', manufacturer: 'Demo', code: 'DEMO-C40', name: 'Demo beton svijetli', finish: 'structured', baseColor: '#b5b2ab', roughness: 0.85, hasGrain: false, procedural: { kind: 'concrete' }, maps: {}, isDemo: true },
  { id: 'demo-hdf-white', manufacturer: 'Demo', code: 'DEMO-H01', name: 'Demo HDF bijeli (leđa)', finish: 'matt', baseColor: '#ecebe6', roughness: 0.8, hasGrain: false, procedural: { kind: 'solid' }, maps: {}, isDemo: true },
];

export function findDecor(id: string): MaterialDecor | undefined {
  return DEMO_DECORS.find((d) => d.id === id);
}
